import type { CallbackEvent } from "@shopify/polaris-types";
import {
  CollapsibleSectionCard,
  SectionHeader,
  NumericField,
} from "../../../components/shared/EditorShared";
import type {
  BundleEditorItem,
  BundlePricingAction,
  BundleType,
} from "../../../store/bundle-editor.mapping";

// ─── Section 4: Pricing ───────────────────────────────────────────────────────

const PRICING_OPTIONS: { value: BundlePricingAction; label: string }[] = [
  { value: "none", label: "Sin descuento" },
  { value: "percentage_off", label: "Porcentaje de descuento" },
  { value: "fixed_amount_off", label: "Descuento fijo" },
];

export function PricingSection({
  bundleType,
  items,
  basePrice,
  compareAtPrice,
  hideCompareAtPrice,
  pricingAction,
  pricingAmount,
  pricingPercentage,
  onBasePriceChange,
  onCompareAtPriceChange,
  onHideCompareAtPriceChange,
  onPricingActionChange,
  onPricingAmountChange,
  onPricingPercentageChange,
}: {
  bundleType: BundleType;
  items: BundleEditorItem[];
  basePrice: number;
  compareAtPrice: number;
  hideCompareAtPrice: boolean;
  pricingAction: BundlePricingAction;
  pricingAmount: number;
  pricingPercentage: number;
  onBasePriceChange: (v: number) => void;
  onCompareAtPriceChange: (v: number) => void;
  onHideCompareAtPriceChange: (v: boolean) => void;
  onPricingActionChange: (v: BundlePricingAction) => void;
  onPricingAmountChange: (v: number) => void;
  onPricingPercentageChange: (v: number) => void;
}) {
  // For fixed bundles the catalog total is derived from item prices ×
  // quantities; we show it as a read-only hint so merchants see the
  // anchor their discount is being applied against. For configurable
  // bundles the merchant sets `base_price` directly since the schema
  // demands a single bundled total regardless of which options the
  // shopper picks.
  const derivedFixedBasePrice = items.reduce(
    (sum, item) => sum + item.basePrice * item.quantity,
    0
  );

  return (
    <CollapsibleSectionCard
      header={<SectionHeader number={5} title="Precios y descuento" />}
    >
      <s-stack direction="block" gap="base">
        <s-text color="subdued">
          Define el precio base del bundle y el descuento que se aplicará.
        </s-text>

        {bundleType === "fixed" ? (
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 flex items-center justify-between">
            <s-text>Precio total a catálogo (calculado):</s-text>
            <s-text type="strong">{derivedFixedBasePrice.toFixed(2)}</s-text>
          </div>
        ) : (
          <NumericField
            label="Precio base del bundle"
            value={basePrice}
            min={0}
            onChange={onBasePriceChange}
          />
        )}

        <NumericField
          label="Precio comparativo (opcional)"
          value={compareAtPrice}
          min={0}
          onChange={onCompareAtPriceChange}
        />

        <s-checkbox
          label="Ocultar precio tachado en el preview"
          checked={hideCompareAtPrice}
          onChange={(e: CallbackEvent<"s-checkbox">) =>
            onHideCompareAtPriceChange(e.currentTarget.checked)
          }
        />

        <s-stack direction="block" gap="small-200">
          <s-text>Tipo de descuento</s-text>
          <div className="flex bg-gray-100 p-1 rounded-lg flex-wrap gap-1">
            {PRICING_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => onPricingActionChange(opt.value)}
                className={`flex-1 py-1.5 px-2 text-xs font-medium rounded-md transition-all whitespace-nowrap ${
                  pricingAction === opt.value
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </s-stack>

        {pricingAction === "percentage_off" && (
          <NumericField
            label="Porcentaje (%)"
            value={pricingPercentage}
            min={0}
            max={100}
            onChange={onPricingPercentageChange}
          />
        )}

        {pricingAction === "fixed_amount_off" && (
          <NumericField
            label="Monto de descuento"
            value={pricingAmount}
            min={0}
            onChange={onPricingAmountChange}
          />
        )}
      </s-stack>
    </CollapsibleSectionCard>
  );
}
